/**
 * components/ui/progress.tsx
 * ───────────────────────────
 * Minimal Shadcn-style Progress bar built with Tailwind.
 * No external dependency — just div + transform for animation.
 */

import { cn } from '@/lib/utils';

interface ProgressProps {
  /** 0–100 */
  value?: number;
  className?: string;
  /** Colour variant. Defaults to the primary brand colour. */
  variant?: 'default' | 'success' | 'warning' | 'destructive';
}

const FILL_COLOURS: Record<NonNullable<ProgressProps['variant']>, string> = {
  default:     'bg-primary',
  success:     'bg-green-500',
  warning:     'bg-amber-500',
  destructive: 'bg-red-500',
};

export function Progress({ value = 0, className, variant = 'default' }: ProgressProps) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      className={cn(
        'relative h-2 w-full overflow-hidden rounded-full bg-muted',
        className,
      )}
    >
      <div
        className={cn(
          'h-full rounded-full transition-all duration-300 ease-out',
          FILL_COLOURS[variant],
        )}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}
